import { FormGroup } from '@angular/forms';
import { DatePickerTypeView } from 'primeng/datepicker';
import type { FormField, ReactiveFieldLogicContext } from './form.interface';

export interface DynamicInputTypeContext<T = unknown> {
    field: FormField<T>;
    model: FormGroup;
    changedField: string | null;
    currentValue: unknown;
    defaultType: InputType;
}

export interface DynamicInputTypeConfiguration<T = unknown> {
    dependsOn?: string | readonly string[];
    defaultType: InputType;
    resolver: (context: DynamicInputTypeContext<T>) => InputType;
}

export interface Input<T> {
    type: InputType;
    isEditable: boolean;
    placeholder?: string;
    data?: T | DropdownData<unknown>;
    defaultValue?: string | boolean | number | Date | T | null | undefined;
    maxLength?: number;
    style?: string;
    optionLabel?: string;
    optionValue?: string;
    textConfiguration?: TextConfiguration;
    validationConfiguration?: InputValidationConfiguration;
    calendarConfiguration?: CalendarConfiguration;
    selectButtonConfiguration?: SelectButtonConfiguration<T>;
    dropdownConfiguration?: DropdownConfiguration<T>;
    textMaskConfiguration?: TextMaskConfiguration;
    twoFieldsConcatConfiguration?: TwoFieldsConcatConfiguration;
    textAreaConfiguration?: TextAreaConfiguration;
    repeatableGroupConfiguration?: RepeatableGroupConfiguration;
    dynamicTypeConfiguration?: DynamicInputTypeConfiguration<T>;
}

export type ReactiveDropdownDataProvenance = 'network' | 'cache' | 'hydrationSeed' | 'manual';

export type DropdownSelection<T> = T | T[] | null;

export interface DropdownData<T> {
    values: Value<T>[];
    filtered: FilteredValue<T>[];
    label?: string;
    /** Origin of the current option list. Hydration seeds must not be persisted as full catalog cache. */
    provenance?: ReactiveDropdownDataProvenance;
}



export interface Value<T> {
    value: T | null;
    label: string;
    /** Original entity used to build this option when `value` is a primitive id. */
    raw?: T | unknown;
    /** Extra primitive ids that can resolve this option while keeping a canonical value. */
    valueAliases?: readonly unknown[];
    group?: string;
    disabled?: boolean;
}

export interface FilteredValue<T> {
    value: T | null;
    label: string;
    /** Original entity used to build this option when `value` is a primitive id. */
    raw?: T | unknown;
    /** Extra primitive ids that can resolve this option while keeping a canonical value. */
    valueAliases?: readonly unknown[];
    group?: string;
    disabled?: boolean;
    tipo?: string;
    unidades?: number | string;
}

export interface TextConfiguration {
    regex: RegExp | null | undefined;
}

export interface InputValidationConfiguration {
    pattern?: RegExp | string;
    patternMessage?: string;
}

export interface CalendarConfiguration {
    calendarView: DatePickerTypeView;
    calendarDateFormat: string;
    disabledDates?: Date[];
    minDate?: Date;
    maxDate?: Date;
    onSelect?: (date: Date) => void;
}

export interface SelectButtonConfiguration<T> {
    isDisabled: boolean;
    isMultiSelect: boolean;
    onClick: (arg0: T) => Promise<object>;
}

export interface DropdownFilterContext<T = unknown> {
    option: FilteredValue<T>;
    field: FormField<T>;
    model: FormGroup;
    changedField: string | null;
    parentValue: unknown;
    currentValue: unknown;
}

export type DropdownFilter<T> = (it: FilteredValue<T>, context?: DropdownFilterContext<T>) => boolean | undefined;

export type ReactiveDropdownAutoSelectMode = 'singleOption' | 'never';

export interface ReactiveLazyDropdownFilterRequest {
    readonly fieldName: string;
    readonly filter: string;
}

export interface DropdownRemoteFilterConfiguration {
    /** Emits typed filters to the host instead of relying only on the local loaded catalog. */
    enabled: boolean;
    /** Minimum typed characters before emitting a remote filter request. Defaults to 2. */
    minLength?: number;
    /** Debounce delay in milliseconds. Defaults to 250. */
    debounceMs?: number;
}

export interface DropdownConfiguration<T> {
    summation?: {
        summand: string;
        name: string;
        uds: string;
        format?: (total: number) => string;
    };
    hasMaxHeight?: boolean;
    /** Legacy object key used by older dropdowns to compare option values. */
    valuePrimaryKey?: string;
    /** Canonical primitive key stored by the Angular FormControl. Falls back to valuePrimaryKey. */
    controlValueKey?: string;
    /** Backend/legacy key used by serializers or payload transforms when it differs from the control value. */
    backendValueKey?: string;
    /** Additional raw keys accepted when resolving previous/backend values against the loaded options. */
    rawIdentityKeys?: readonly string[];
    autoSelect?: ReactiveDropdownAutoSelectMode;
    /** Enables PrimeNG virtual scroll for large local catalogs. */
    virtualScroll?: boolean;
    /** Item height used by PrimeNG virtual scroll. Defaults to 40. */
    virtualScrollItemSize?: number;
    /** Minimum typed characters required before rendering the full local option list. */
    minFilterLengthBeforeFullRender?: number;
    /** Maximum number of local options rendered while the typed filter is still too short. */
    maxRenderedOptionsBeforeFilter?: number;
    /** Optional remote filtering hook for large catalogs. */
    remoteFilter?: DropdownRemoteFilterConfiguration;
    distinctBy?: string | readonly string[] | ((option: FilteredValue<T>) => unknown);
    filter?: DropdownFilter<T>;
    /** Re-runs a local filter for every control change. Defaults to false when no explicit dependencies exist. */
    filterOnEveryChange?: boolean;
    filteredValues?: FilteredValue<T>[];
    swap?: (it: FormField<T>, context?: ReactiveFieldLogicContext<T>) => void;
    appendData?: (it: FormField<T>, context?: ReactiveFieldLogicContext<T>) => void | Promise<void>;
    transform?: ((value: FormField<T>, model: FormGroup) => T)[];
}

export interface TextMaskConfiguration {
    mask: string;
}

export interface TwoFieldsConcatConfiguration {
    subField1: string;
    separator: string;
    subField2: string;
}

export interface TextAreaConfiguration {
    rows?: number;
    cols?: number;
    hasVoiceTranscription: boolean;
    canTakePhoto: boolean;
    photoField: string | undefined;
}

export type RepeatableSubFieldContext = {
    rowIndex: number;
    row: Record<string, unknown>;
    rows: Record<string, string>[];
    model: FormGroup;
    /** Resolve the full raw entity for a repeatable dropdown whose row value stores only the primitive id. */
    resolveEntity?: <TEntity extends Record<string, unknown>>(key: string) => TEntity | null;
};

export type RepeatableSubFieldDependencyBehaviour = {
    clearOnParentChange?: boolean;
    autoSelect?: 'singleOption' | false;
    keepCurrentValueIfStillAvailable?: boolean;
};

export type RepeatableSubFieldDependencyContext = RepeatableSubFieldContext & {
    changedKey: string;
    changedValue: unknown;
    subField: RepeatableSubField;
    parentField: RepeatableSubField | null;
};

export type RepeatableSubFieldDataLoader = (
    ctx: RepeatableSubFieldDependencyContext,
) => void | DropdownData<unknown> | Promise<void | DropdownData<unknown>>;

export type RepeatableSubFieldDependencyPatch = Record<string, unknown>;

export type RepeatableSubFieldDependencyPatchFactory = (
    ctx: RepeatableSubFieldDependencyContext,
) => void | RepeatableSubFieldDependencyPatch | Promise<void | RepeatableSubFieldDependencyPatch>;


export interface RepeatableSubFieldDataSourceContext {
    readonly rowIndex: number;
    readonly row: Record<string, unknown>;
    readonly rows: Record<string, string>[];
    readonly model: FormGroup;
    readonly subField: RepeatableSubField;
}

export type RepeatableSubFieldDataSourceResponseMapper = (
    response: unknown,
    context: RepeatableSubFieldDataSourceContext,
) => DropdownData<unknown> | Value<unknown>[] | unknown[];

export type RepeatableSubFieldDataSourceOptionMapper = (
    item: unknown,
    context: RepeatableSubFieldDataSourceContext,
) => Value<unknown>;

export type RepeatableSubFieldDataSourceKeySelector =
    string |
    readonly string[] |
    ((item: unknown, context: RepeatableSubFieldDataSourceContext) => unknown);

export interface RepeatableSubFieldDataSource {
    readonly endpoint: unknown;
    readonly cacheKey: string;
    readonly responsePath?: string;
    readonly labelKey?: string;
    readonly labelKeys?: readonly string[];
    readonly valueKey?: string;
    readonly valueKeys?: readonly string[];
    readonly distinctBy?: RepeatableSubFieldDataSourceKeySelector;
    readonly ttlMs?: number;
    readonly timeoutMs?: number;
    readonly forceReload?: boolean;
    readonly cacheEmptyResults?: boolean;
    readonly mapResponse?: RepeatableSubFieldDataSourceResponseMapper;
    readonly mapOption?: RepeatableSubFieldDataSourceOptionMapper;
}

export type RepeatableSubField = {
    key: string;
    label?: string;
    input: Input<unknown>;
    isVisible?: () => boolean;
    computeValue?: (ctx: RepeatableComputeContext) => string;
    getData?: (ctx: RepeatableSubFieldContext) => DropdownData<unknown>;
    dataSource?: RepeatableSubFieldDataSource;
    dependsOn?: string | readonly string[];
    /** Parent FormGroup controls that affect this row field data/options. */
    parentDependencies?: string | readonly string[];
    dependencyBehaviour?: RepeatableSubFieldDependencyBehaviour;
    loadData?: RepeatableSubFieldDataLoader;
    onDependencyChange?: RepeatableSubFieldDependencyPatchFactory;
};

export type RepeatableRowExtraItem = {
    label: string;
    value: string;
    styleClass?: string;
};

export type RepeatableRowExtraView = {
    isVisible: boolean;
    styleClass?: string;
    items: RepeatableRowExtraItem[];
};

export type RepeatableInfoDialogTable = {
    headers: string[];
    rows: string[][];
    styleClass?: string;
};

export type RepeatableInfoDialogSection = {
    title?: string;
    tables: RepeatableInfoDialogTable[];
};

export type RepeatableInfoDialogView = {
    header: string;
    sections: RepeatableInfoDialogSection[];
};

export type RepeatableDomainAdapterContext = {
    rowIndex: number;
    row: Record<string, unknown>;
    rows: Record<string, string>[];
    model: FormGroup | null;
    subFields: RepeatableSubField[];
    resolveEntity: <T extends Record<string, unknown>>(key: string) => T | null;
};

export interface RepeatableGroupDomainAdapter {
    getNumericInputClasses?: (ctx: RepeatableDomainAdapterContext, key: string) => Record<string, boolean>;
    getRowExtraView?: (ctx: RepeatableDomainAdapterContext) => RepeatableRowExtraView;
    canOpenInfoDialog?: (ctx: RepeatableDomainAdapterContext) => boolean;
    getInfoDialogView?: (ctx: RepeatableDomainAdapterContext) => RepeatableInfoDialogView | null;
}

export type RepeatableLegacySlotTarget = {
    fieldName: string;
    emptyValue?: unknown;
};

export type RepeatableLegacySlotTargetInput = string | RepeatableLegacySlotTarget;

export type RepeatableLegacySlotSerialization = {
    targets: Record<string, RepeatableLegacySlotTargetInput | RepeatableLegacySlotTargetInput[]>;
    rows?: number;
    removeBindingFields?: boolean;
};

export type RepeatableLegacyAggregateTarget = {
    fieldName: string;
    emptyValue?: unknown;
};

export type RepeatableLegacyAggregateTargetInput = string | RepeatableLegacyAggregateTarget;

export type RepeatableLegacyAggregateSerialization = {
    /**
     * Optional target map. When omitted, the repeatable bindings are used.
     * Use this when a backend field name differs from the logical binding field.
     */
    targets?: Record<string, RepeatableLegacyAggregateTargetInput | RepeatableLegacyAggregateTargetInput[]>;
    separator?: string;
    trimTrailingEmptyRows?: boolean;
    /**
     * Logical subfield or subfields used to decide how many trailing rows are kept.
     * Useful for legacy semicolon payloads where optional columns must stay aligned with a required row anchor.
     */
    trimTrailingEmptyRowsBy?: string | readonly string[];
};


export type RepeatablePendingDropdownResolutionConfiguration = {
    /** Maximum scheduled attempts before keeping unresolved values pending. Defaults to 200. */
    maxAttempts?: number;
    /** Fallback retry delay in milliseconds for legacy asynchronous option sources. Defaults to 50. */
    retryDelayMs?: number;
    /** Maximum dependent-dropdown cascade passes inside a single attempt. Defaults to 25. */
    cascadePassLimit?: number;
    /** Maximum consecutive attempts without progress before stopping fallback polling. Defaults to 3. */
    stalledAttemptLimit?: number;
};

export type RepeatableGroupConfiguration = {
    rows: number;
    addButtonLabel: string;
    bindings: Record<string, string>;
    serialization: {
        separator: string;
    };
    subFields: RepeatableSubField[];
    /** Parent FormGroup controls that must be watched before/while hydrating rows. */
    parentDependencies?: string | readonly string[];
    /** Retry policy for pending dropdown values restored before their options are available. */
    pendingDropdownResolution?: RepeatablePendingDropdownResolutionConfiguration;
    /** Forces the card/stacked layout even on desktop. Useful for narrow side sections. */
    forceStackedLayout?: boolean;
    domainAdapter?: RepeatableGroupDomainAdapter;
    legacySlotSerialization?: RepeatableLegacySlotSerialization;
    legacyAggregateSerialization?: RepeatableLegacyAggregateSerialization;
};

export type RepeatableComputeContext = {
    rowIndex: number;
    rows: Record<string, string>[];
    model: FormGroup;
};

export enum InputType {
    HIDDEN,
    EMPTY,
    EXTERNAL,
    ONLY_TEXT,
    TEXT,
    NUMERIC,
    PASSWORD,
    TEXT_MASK,
    TWO_FIELDS_CONCAT,
    TEXTAREA,
    DROPDOWN,
    DROPDOWN_SEARCH,
    MULTISELECT,
    MULTISELECT_SEARCH,
    TIME,
    CALENDAR,
    SWITCH,
    SELECT_BUTTON,
    REPEATABLE_GROUP,
}
